/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  CompanyContainer,
  HeaderTitle,
  StyledContainer,
  TableCell,
  TableWrapper,
  IconButton,
  LoaderWrapper,
} from '../../../services/settings/appStyle/global.style';

import {
  Table,
  IconButton as IconButtonWithText,
  DeleteConfirmationModal,
  Loader,
  DropdownButton,
} from '../../../Components/uielements';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { useUserContext } from '../../../hooks/UserContext';
import GeofenceService, {
  listAllGeofencesByManager,
  listAllGeofencesByManagerForReport,
} from '../../../services/bioplus-services/geofence';
// import PremiseService from '../../../services/bioplus-services/premise';
import { DeleteIcon, EyeIcon, WhiteLocationIcon } from '../../../Components/icons';
import moment from 'moment';
import { HeaderButtonWrapper } from './geofence.style';
import { useNavigate } from 'react-router-dom';
import { IntlMessages } from '../../../helper/langHelper';
import { exportExcelFile } from '../../../helper/exportExcelHelper';

export function extractData(dataArray) {
  if (dataArray && dataArray.length > 0) {
    const extractedData = dataArray?.map((item) => {
      return {
        GeofenceName: item?.name,
        CompanyName: item?.premiseId?.propertyName,
        DeclarationType: item?.declarationType,
        speed: item?.speedLimit,
        Creator: item?.createdBy,
        createdAt: moment(item?.createdAt).format('DD-MM-YYYY'),
      };
    });
    return { bioplusUsers: extractedData };
  }
}

const ManagerGeofence = () => {
  const userContext = useUserContext();
  const userRole = userContext.company;
  const queryClient = useQueryClient();
  const companyId = userRole.companyId?.id;
  const navigate = useNavigate();

  const [deleteGeofence, setDeleteGeofence] = useState();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const premiseId = userRole?.premiseId;

  const geofenceQuery = useQuery(['managerGeofences', page, pageSize], () =>
    listAllGeofencesByManager(userContext.company?.companyId?.id, premiseId, page, pageSize)
  );

  const allGeofenceForReport = useQuery(['allManagerGeofences'], () =>
    listAllGeofencesByManagerForReport(userContext.company?.companyId?.id, premiseId)
  );

  //   const { data: premisesData } = useQuery(['premises', companyId], () =>
  //     PremiseService.listAllByCompany(companyId, {})
  //   );

  const deleteMutation = useMutation({
    mutationFn: (geofence) => {
      return GeofenceService.remove(geofence);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managerGeofences'] });
    },
  });

  const geofenceTableData = geofenceQuery?.data?.geofences ?? [];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleDeleteGeofence = (geofence) => {
    deleteMutation.mutate(geofence);
    setDeleteGeofence(undefined);
  };

  // Role based permission check for the "geofence" module
  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find(
    (permission) => permission?.label === 'geofence'
  );

  const canManageGeofence = !!isUserAllowToManage?.isManage;
  const canDownloadReport = !!isUserAllowToManage?.isReport;

  //   table coulmns

  const userColumns = useMemo(() => {
    const cols = [
      {
        Header: <IntlMessages id="table.col.geofence" />,
        accessor: 'name',
        width: '15%',
      },
      {
        Header: <IntlMessages id="table.col.premise" />,
        accessor: 'premiseId.propertyName',
        Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
        width: '15%',
      },
      {
        Header: <IntlMessages id="table.col.dec.type" />,
        accessor: 'declarationType',
        Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
        width: '12%',
      },
      {
        Header: <IntlMessages id="table.col.speed.limit" />,
        accessor: 'speedLimit',
        Cell: (cellData) => <TableCell>{cellData.value ? cellData.value : '--'}</TableCell>,
        width: '12%',
      },

      {
        Header: <IntlMessages id="table.col.creator" />,
        accessor: 'createdBy',
        width: '18%',
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        width: '15%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
    ];

    // Actions column contains View (always available) + Delete (manage-only);
    // column itself always renders since View has no permission gate.
    cols.push({
      Header: <IntlMessages id="table.col.actions" />,
      accessor: 'actions',
      isAction: true,
      width: '20%',
    });

    return cols;
  }, []);

  const handleNavigationToMap = (data) => {
    if (data) {
      navigate('/geofenceMap', { state: { data } });
    }
  };

  const actionList = [
    (data) => (
      <IconButton onClick={() => handleNavigationToMap(data)}>
        <EyeIcon style={{ width: '22px', height: '22px' }} />
      </IconButton>
    ),
    ...(canManageGeofence
      ? [
          (data) => (
            <IconButton onClick={() => setDeleteGeofence(data)}>
              <DeleteIcon style={{ width: '20px', height: '20px' }} />
            </IconButton>
          ),
        ]
      : []),
  ];

  const excelFormatData = extractData(allGeofenceForReport?.data);
  const fileName = `Geofences-${userRole?.companyId?.name}-${moment().format(
    'DD-MM-YYYY-hh.mm A'
  )}.xlsx`;
  const fileNameCsv = `Geofences-${userRole?.companyId?.name}-${moment().format(
    'DD-MM-YYYY-hh.mm A'
  )}.csv`;
  const columnConfig = [
    { header: 'Geofence Name', key: 'GeofenceName', width: 30 },
    { header: 'Property Name', key: 'CompanyName', width: 30 },
    { header: 'Declaration Type', key: 'DeclarationType', width: 20 },
    { header: 'Speed Limit', key: 'speed', width: 10 },
    { header: 'Creator', key: 'Creator', width: 30 },
    { header: 'Created Date', key: 'createdAt', width: 20 },
  ];

  const handleClickItem = (item) => {
    // Guard: even if triggered programmatically, block export without report permission
    if (!canDownloadReport) return;

    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.geofences" />
        </HeaderTitle>
        <HeaderButtonWrapper>
          {excelFormatData && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
          <IconButtonWithText
            text={<IntlMessages id="button.text.viewMap" />}
            icon={<WhiteLocationIcon style={{ width: '20px', height: '19px' }} />}
            onClick={() => navigate('/geofenceMap', { state: { isView: true } })}
          />
        </HeaderButtonWrapper>
      </CompanyContainer>

      {geofenceQuery.isLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        <>
          {' '}
          <TableWrapper>
            <Table
              data={geofenceTableData}
              columns={userColumns}
              hasFooter={true}
              page={page}
              pageSize={pageSize}
              prevPage={handlePreviousPage}
              nextPage={handleNextPage}
              total={geofenceQuery?.data?.totalGeofence || 0}
              actions={actionList}
            />
          </TableWrapper>
        </>
      )}

      {deleteGeofence && canManageGeofence && (
        <DeleteConfirmationModal
          onClose={() => setDeleteGeofence(undefined)}
          message={
            <IntlMessages
              id="geofence.delete.confirmation"
              values={{ geofence: deleteGeofence.name }}
            />
          }
          onConfirm={() => handleDeleteGeofence(deleteGeofence)}
        />
      )}
    </StyledContainer>
  );
};

export default ManagerGeofence;
